"use client";

import { LegalPageLayout } from "@/components/legal/legal-page-layout";
import { useTranslations } from "@/hooks/use-translations";

export default function CookiesPage() {
  const { t } = useTranslations("legal");
  
  return (
    <LegalPageLayout title={t("cookiePolicyLabel")} lastUpdated="October 2025">
      <div className="prose prose-neutral max-w-none space-y-6 text-neutral-700">
          <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-4 mb-6">
            <p className="text-sm text-amber-900">
              <strong>{t("governingLanguage")}</strong>
            </p>
          </div>
          
          <p>
            GrowthSync uses cookies and similar technologies to enhance your experience on the Platform. This Cookie Policy explains what cookies are, how we use them, and your choices regarding their use.
          </p>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">1. What are cookies?</h2>
            <p>
              Cookies are small text files stored on your device when you visit a website or use an app. They help remember your preferences and improve functionality.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">2. How we use cookies</h2>
            <p>
              GrowthSync uses cookies to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Keep you signed in</li>
              <li>Remember your preferences</li>
              <li>Improve performance and loading speed</li>
              <li>Analyse usage to improve the Platform</li>
              <li>Support security and fraud prevention</li>
            </ul>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3. Types of cookies we use</h2>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Essential cookies</strong> – required for login, security, and core features. These cannot be disabled as they are necessary for the Platform to function.</li>
              <li><strong>Preference cookies</strong> – remember user settings like theme or filters. These enhance your experience but are not essential for basic functionality.</li>
              <li><strong>Analytics cookies</strong> – help us understand app usage and improve performance. These are optional and can be disabled.</li>
              <li><strong>Third-party cookies</strong> – used by trusted service providers such as payment processors when applicable. These are necessary for certain features to work.</li>
            </ul>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">3a. Cookie duration</h2>
            <p>
              Cookies may be either "session" cookies or "persistent" cookies:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li><strong>Session cookies</strong> – temporary cookies that expire when you close your browser or log out.</li>
              <li><strong>Persistent cookies</strong> – remain on your device for a set period or until you delete them. These are used to remember your preferences and login status.</li>
            </ul>
            <p>
              Most authentication cookies are session-based for security, while preference cookies may persist for up to one year.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">4. Cookie consent</h2>
            <p>
              When you first visit GrowthSync, you will be asked to consent to our use of cookies. You can change your cookie preferences at any time through your account settings or browser settings.
            </p>
            <p>
              Essential cookies do not require consent as they are necessary for the Platform to function. You can opt out of non-essential cookies, though this may affect certain features and functionality.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">5. Managing cookies</h2>
            <p>
              You can adjust your browser or device settings to block or delete cookies. Most browsers allow you to:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>View what cookies are stored on your device.</li>
              <li>Delete cookies individually or all at once.</li>
              <li>Block cookies from specific sites or all sites.</li>
              <li>Block third-party cookies only.</li>
              <li>Delete cookies when you close your browser.</li>
            </ul>
            <p>
              However, disabling essential cookies may impact app functionality and prevent you from using certain features.
            </p>
            <p>
              Instructions for managing cookies in popular browsers are available in their respective help sections.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">6. Updates to this policy</h2>
            <p>
              We may update this Cookie Policy from time to time. The latest version will always be available in the app.
            </p>
          </section>

          <section>
            <h2 className="gs-h2 text-neutral-900 mt-8 mb-4">Contact</h2>
            <p>
              For questions, contact: <a href="mailto:contact@growth-sync.com" className="text-blue-600 hover:text-blue-700 hover:underline">contact@growth-sync.com</a>
            </p>
          </section>
        </div>
    </LegalPageLayout>
  );
}
